import { type PropsWithChildren, useEffect, useState } from "react";
import { LazyLoading } from "@b2bportal/core-ui";
import { useSessionContext } from "@/components/AuthModule/SessionContext";
import {
  buildCommerceTripsUrl,
  hardNavigate,
  isCommerceTripsEnabled,
  resolveLegacyTripRedirect,
  shouldReloadToCommerce,
} from "@/utils/commerce/trips";

const TRIPS_LIST_PATH = /^\/trips\/?$/;

/**
 * Safety net for in-app navigations to legacy `/trips` URLs once
 * commerce-portals serves them:
 * - Flag off: renders the legacy Trips app.
 * - Flag on and the URL is a legacy trip details/cancel link PerkSpot's own
 *   sites still point at: redirects straight to its commerce-portals
 *   equivalent (no reload guard needed, since `/stays/` always routes there).
 * - Flag on and the URL is the trips list: reloads the current URL so the
 *   gateway routes it, guarded against a loop; if the gateway still serves
 *   this path, logs and renders the legacy Trips app instead.
 * - Flag on, any other `/trips/*` path (unmapped sub-paths the gateway still
 *   sends here): sends the user straight to the commerce-portals trips list,
 *   since these unmapped paths have no commerce gateway route.
 */
export const CommerceTripsRoute = ({ children }: PropsWithChildren) => {
  const { sessionInfo } = useSessionContext();

  // PerkSpot doesn't render under StrictMode, so this initializer runs once.
  // Under StrictMode its second call would trip the guard.
  const [target] = useState<string | null>(() => {
    if (!isCommerceTripsEnabled()) return null;

    const attribution = {
      perkspotUserId: sessionInfo?.userDetails.tenantUserId,
      communityId: sessionInfo?.userDetails.communityId,
    };

    const redirectUrl = resolveLegacyTripRedirect(
      window.location.pathname,
      attribution
    );
    if (redirectUrl) return redirectUrl;

    if (!TRIPS_LIST_PATH.test(window.location.pathname)) {
      return buildCommerceTripsUrl(attribution);
    }

    const allowed = shouldReloadToCommerce(window.location.href);
    if (!allowed) {
      console.error(
        "Commerce trips is enabled but /trips is still served by b2bportal; rendering the legacy Trips app."
      );
      return null;
    }
    return window.location.href;
  });

  useEffect(() => {
    if (target) hardNavigate(target, "replace");
  }, [target]);

  return target ? <LazyLoading /> : <>{children}</>;
};
